function safeText(value, maxLength = 160) {
  return String(value ?? '').slice(0, maxLength)
}

function safeHttpUrl(value) {
  const source = safeText(value, 2048).trim()
  if (!source) return ''
  try {
    const parsed = new URL(source)
    return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? parsed.href : ''
  } catch (_) {
    return ''
  }
}

function safeMiniProgramPath(value) {
  const source = safeText(value, 512).trim()
  if (!source || !source.startsWith('/') || source.startsWith('//') || source.includes('://')) return ''
  for (const character of source) {
    const codePoint = character.codePointAt(0)
    if (codePoint <= 31 || codePoint === 127) return ''
  }
  return source
}

function normalizeProductCard(card, index) {
  const source = card && typeof card === 'object' ? card : {}
  const result = {
    id: safeText(source.productId || source.goodsId || source.id || `product-${index}`, 128),
    name: safeText(source.name || source.productName || '未命名商品', 120),
    price: safeText(source.price ?? source.salePrice ?? '', 32),
    subtitle: safeText(source.subtitle || source.path || '', 160),
    imageUrl: safeHttpUrl(source.image || source.picture || source.imageUrl || ''),
    href: safeHttpUrl(source.url || source.href || ''),
  }
  const wxAppId = safeText(source.wxAppId || source.wxappId || '', 128).trim()
  const path = safeMiniProgramPath(source.path)
  const tuanzhangId = safeText(source.tuanzhangId || '', 128).trim()
  if (wxAppId) result.wxAppId = wxAppId
  if (path) result.path = path
  if (tuanzhangId) result.tuanzhangId = tuanzhangId
  return result
}

/** PC cannot launch WeChat Mini Programs directly; expose safe launch details instead. */
export function productCardAction(card) {
  const href = safeHttpUrl(card?.href)
  if (href) return { kind: 'web', href }
  const appId = safeText(card?.wxAppId, 128).trim()
  const path = safeMiniProgramPath(card?.path)
  if (appId && path) return { kind: 'mini_program', appId, path }
  return { kind: 'none' }
}

export function normalizeStructuredData(payload) {
  if (!payload || typeof payload !== 'object') return null
  if (Array.isArray(payload) ? payload.length === 0 : Object.keys(payload).length === 0) return null
  if (payload.kind === 'artifact_job') {
    const source = payload.data && typeof payload.data === 'object' ? payload.data : payload
    const jobId = safeText(source.jobId, 128)
    const skillCode = safeText(source.skillCode, 64)
    const operationKind = safeText(source.operationKind, 16)
    const status = safeText(source.status, 64)
    const revision = Number(source.revision)
    if (jobId && ['document.word.create', 'document.ppt.create', 'spreadsheet.excel.process'].includes(skillCode)
      && ['create', 'analyze'].includes(operationKind) && Number.isInteger(revision) && revision > 0) {
      return { kind: 'artifact_job', jobId, skillCode, operationKind, status, revision }
    }
    return null
  }
  const isProductCards = payload.kind === 'product_cards' || payload.type === 'goods_cards'
  if (!isProductCards) {
    return { kind: 'unsupported', label: '已收到暂不支持的结构化结果' }
  }
  const items = Array.isArray(payload.data)
    ? payload.data
    : (Array.isArray(payload.cards) ? payload.cards : [])
  return {
    kind: 'product_cards',
    cards: items.slice(0, 10).map(normalizeProductCard),
  }
}

export function updateToolProgress(current, event) {
  const list = Array.isArray(current) ? current.slice() : []
  if (!event?.toolCallId || !['tool_start', 'tool_done'].includes(event.type)) return list
  const next = {
    toolCallId: safeText(event.toolCallId, 128),
    toolName: safeText(event.toolName, 128),
    label: safeText(event.label || (event.type === 'tool_start' ? '正在执行工具' : '工具执行完成'), 160),
    status: event.type === 'tool_start' ? 'running' : (event.status || 'completed'),
  }
  const index = list.findIndex(item => item.toolCallId === next.toolCallId)
  if (index >= 0) list.splice(index, 1, next)
  else list.push(next)
  return list
}

export function toUiChatEvent(event) {
  if (!event || typeof event !== 'object') return null
  const payload = event.payload && typeof event.payload === 'object' ? event.payload : {}
  switch (event.type) {
    case 'run_started': {
      // HMS 先持久化用户消息再发此事件；失败重试必须使用这个真实游标。
      const messageId = String(payload.userMessageId || '')
      return messageId ? { type: 'user_persisted', msgId: messageId } : null
    }
    case 'text_delta':
      return { type: 'text_chunk', content: String(payload.text || '') }
    case 'tool_started':
      return {
        type: 'tool_start',
        toolCallId: String(payload.toolCallId || ''),
        toolName: String(payload.toolName || ''),
        label: String(payload.label || payload.toolName || '处理中'),
      }
    case 'tool_completed':
      {
        const output = payload.output && typeof payload.output === 'object' ? payload.output : {}
        const failed = output.ok === false
        return {
          type: 'tool_done',
          toolCallId: String(payload.toolCallId || ''),
          toolName: String(payload.toolName || ''),
          label: safeText(payload.label || output.message || ''),
          status: failed ? 'failed' : 'completed',
        }
      }
    case 'ui_data':
      return { type: 'structured_data', data: normalizeStructuredData(payload) }
    case 'run_completed': {
      const messageId = String(payload.assistantMessageId || '')
      return { type: 'done', msgId: messageId, msgGuid: messageId, messageStatus: 'completed' }
    }
    case 'run_failed':
      return {
        type: 'error',
        message: payload.incomplete ? '回答未完成，可重试' : '生成失败，请重试',
        incomplete: Boolean(payload.incomplete),
        messageStatus: 'failed',
        msgGuid: String(payload.assistantMessageId || ''),
      }
    case 'run_cancelled':
      return {
        type: 'cancelled',
        message: '已停止生成',
        incomplete: true,
        messageStatus: 'cancelled',
        msgGuid: String(payload.assistantMessageId || ''),
      }
    default:
      return null
  }
}

export function createChatEventAdapter(onEvent) {
  let executionId = ''
  let lastSequence = 0

  return {
    push(event) {
      if (!event || typeof event.sequence !== 'number') return false
      const incomingExecutionId = String(event.executionId || '')
      const isPreAdmissionFailure = event.type === 'run_failed' && !incomingExecutionId && !executionId
      if (!incomingExecutionId && !isPreAdmissionFailure) return false
      if (executionId && executionId !== incomingExecutionId) return false
      if (!executionId && incomingExecutionId) executionId = incomingExecutionId
      if (event.sequence <= lastSequence) return false

      lastSequence = event.sequence
      const mapped = toUiChatEvent(event)
      if (mapped) {
        if (mapped.type === 'structured_data' && mapped.data?.kind === 'artifact_job') {
          mapped.executionId = executionId
        }
        onEvent(mapped)
      }
      return true
    },
    executionId: () => executionId,
    lastSequence: () => lastSequence,
  }
}
